import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '~/actions';
import Background from '~/components/Background';
import Button from '~/components/Button';
import Cardbox from '~/components/Cardbox';
import CardboxWrapper from '~/components/CardboxWrapper';
import Divider from '~/components/Divider';
import Gap from '~/components/Gap';
import GoogleLoginButton from '~/components/GoogleLoginButton';
import PasswordInput from '~/components/PasswordInput';
import StyledInput from '~/components/StyledInput';
import StyledLink from '~/components/StyledLink';
import Text from '~/components/Text';
import { SIGNUP_ROUTE } from '~/constants/routes';
import { STATUS } from '~/literals';
import { useAppSelector } from '~/modules/hooks';
import { selectUser } from '~/selectors';
import { useIsDesktop } from '~/hooks/useIsDesktop';

const GoogleLoginWrapper = styled.div`
  > div {
    width: 100% !important;
  }

  div#content {
    flex: 1;
    width: 100% !important;
    max-width: 100%;
  }

  iframe {
    width: 100% !important;
  }
`;

const Login = () => {
  const dispatch = useDispatch();
  const user = useAppSelector(selectUser);
  const isDesktop = useIsDesktop();

  const isLoading = user.status == STATUS.RUNNING;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const isEnabled = useMemo(
    () => Boolean(isLoading || (email.trim() && password.trim())),
    [isLoading, email, password],
  );

  const handleClickLogin = () => {
    if (!isEnabled) {
      toast.error('Email and Password are mandatory');
      return;
    }
    dispatch(login({ email, password }));
  };

  const navigate = useNavigate();

  const handleSignupRedirect = () => {
    navigate(SIGNUP_ROUTE);
  };

  return (
    <Background key="login" data-component-name="Login">
      <CardboxWrapper
        className="center"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            handleClickLogin();
          }
        }}
      >
        <Cardbox>
          <Text align="center" size={isDesktop ? 'lg' : 'md'} className="bold">
            Log in to createmyinvoice
          </Text>
          <Gap t={56} />
          <GoogleLoginWrapper className="full-width center">
            <GoogleLoginButton />
          </GoogleLoginWrapper>
          <div className="full-width center">
            <Divider margin="20px 12px 20px 0" className="full-width full-flex" />
            <Text size="xs" variant="neutral" className="bold">
              OR
            </Text>
            <Divider margin="20px 0 20px 12px" className="full-width full-flex" />
          </div>
          <Text size="xs">Email</Text>
          <Gap t={6} />
          <StyledInput
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="e.g. john@company.com"
            name="email"
            autoComplete="username"
          />
          <Gap t={16} />
          <div className="full-width spread align-center">
            <Text size="xs">Password</Text>
            <StyledLink size="xms" variant="secondary" to="/forgotpassword">
              Forgot Password?
            </StyledLink>
          </div>

          <Gap t={6} />
          <PasswordInput
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="**************"
            name="password"
            autoComplete="current-password"
          />
          <Gap t={24} />
          <Button disabled={!isEnabled} onClick={handleClickLogin} size="full">
            {isLoading ? <LoadingOutlined /> : 'Log In'}
          </Button>
          <Divider margin="32px 0" />
          <div className="full-width spread align-center">
            <Text>Don't have an account?</Text>
            <Gap l={4} />
            <Button onClick={handleSignupRedirect} variant="secondary">
              Sign up
            </Button>
          </div>
        </Cardbox>
      </CardboxWrapper>
    </Background>
  );
};

export default Login;
